import { ErrorCode, FileRejection } from "react-dropzone";

/**
 * Espelha AnexoUploadPolicy.cs (backend). Mantenha os dois em sincronia —
 * extensões, limites e o texto das mensagens.
 *
 * Há um gêmeo deste arquivo no portal do colaborador: os dois portais enviam
 * anexos para a mesma policy, mas não compartilham código.
 */

/** Extensões aceitas, sem ponto e em minúsculas. */
export const EXTENSOES_PERMITIDAS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "doc",
  "docx",
  "xls",
  "xlsx",
  // CAD/GIS: o construtor de formulário já oferece os três na pergunta de Anexo,
  // e planta arquitetônica é o anexo central de um alvará de construção.
  "dwg",
  "dxf",
  "shp"
];

/** 25 MB por arquivo. */
export const TAMANHO_MAXIMO_BYTES = 25 * 1024 * 1024;

/** 10 arquivos por envio. */
export const QUANTIDADE_MAXIMA = 10;

const TAMANHO_MAXIMO_MB = TAMANHO_MAXIMO_BYTES / (1024 * 1024);

const listar = (extensoes: string[]): string =>
  extensoes.map((extensao) => extensao.toUpperCase()).join(", ");

/** As três regras da policy em uma linha, para exibir no campo. */
export const descricaoLimites = (
  extensoes: string[] = EXTENSOES_PERMITIDAS
): string => {
  const tipos = listar(extensoes);

  return `${tipos} / até ${TAMANHO_MAXIMO_MB} MB por arquivo / no máximo ${QUANTIDADE_MAXIMA} arquivos`;
};

export const DESCRICAO_LIMITES = descricaoLimites();

/** Estourar a quantidade não é culpa de um arquivo específico do lote. */
export const MENSAGEM_QUANTIDADE = `Você pode anexar no máximo ${QUANTIDADE_MAXIMA} arquivos.`;

const mensagemExtensao = (nome: string, extensoes: string[]): string =>
  `"${nome}": extensão não permitida. Envie ${listar(extensoes)}.`;

const mensagemTamanho = (nome: string): string =>
  `"${nome}" passa de ${TAMANHO_MAXIMO_MB} MB.`;

export const extrairExtensao = (nomeArquivo: string): string | null => {
  const ultimoPonto = nomeArquivo.lastIndexOf(".");

  if (ultimoPonto === -1) return null;

  return nomeArquivo.slice(ultimoPonto + 1).toLowerCase();
};

/**
 * A policy é o piso: o `accept` configurado na questão só pode restringir a
 * lista, nunca ampliar o que o backend aceita.
 */
export const intersectarExtensoes = (accept?: string[]): string[] => {
  if (!accept || accept.length === 0) return EXTENSOES_PERMITIDAS;

  const pedidas = accept.map((extensao) =>
    extensao.trim().replace(/^\./, "").toLowerCase()
  );

  return EXTENSOES_PERMITIDAS.filter((extensao) => pedidas.includes(extensao));
};

/** Formato de `accept` do react-dropzone 12.x: extensões, não tipos MIME. */
export const acceptDropzone = (extensoes: string[]): string =>
  extensoes.map((extensao) => `.${extensao}`).join(",");

/** Mesmo nome não significa mesmo arquivo — a identidade inclui o conteúdo. */
export const identidadeArquivo = (arquivo: File): string =>
  `${arquivo.name}|${arquivo.size}|${arquivo.lastModified}`;

/** Valida o arquivo antes do upload; retorna a mensagem de erro ou null. */
export const validarAnexo = (
  file: File,
  jaSelecionados: number,
  extensoes: string[] = EXTENSOES_PERMITIDAS
): string | null => {
  if (jaSelecionados >= QUANTIDADE_MAXIMA) return MENSAGEM_QUANTIDADE;

  const extensao = extrairExtensao(file.name);

  if (!extensao || !extensoes.includes(extensao))
    return mensagemExtensao(file.name, extensoes);

  if (file.size > TAMANHO_MAXIMO_BYTES) return mensagemTamanho(file.name);

  return null;
};

/**
 * Traduz as rejeições do react-dropzone. "too-many-files" marca todos os
 * arquivos do lote de uma vez, então rende uma mensagem só em vez de uma por
 * arquivo.
 */
export const mensagensRejeicao = (
  rejeicoes: FileRejection[],
  extensoes: string[] = EXTENSOES_PERMITIDAS
): string[] => {
  const mensagens: string[] = [];
  let estourouLote = false;

  rejeicoes.forEach(({ file, errors }) => {
    const codigos = errors.map((erro) => erro.code);

    if (codigos.includes(ErrorCode.TooManyFiles)) {
      estourouLote = true;
      return;
    }

    if (codigos.includes(ErrorCode.FileInvalidType)) {
      mensagens.push(mensagemExtensao(file.name, extensoes));
      return;
    }

    mensagens.push(mensagemTamanho(file.name));
  });

  if (estourouLote) mensagens.unshift(MENSAGEM_QUANTIDADE);

  return mensagens;
};
